/**
 * ConstanciaViewerModal
 * Renderiza inline el PDF de la Constancia Cesión Parcial usando un iframe
 * sobre el presigned URL de S3. Mismo patrón que SignedContractViewerModal.
 */

import { ExternalLink, Loader2, AlertCircle } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  ccId: string;
  url: string | null;
  isLoading: boolean;
  errorMessage?: string | null;
}

export function ConstanciaViewerModal({ isOpen, onClose, ccId, url, isLoading, errorMessage }: Props) {
  const title = `Constancia Cesión Parcial — CC ${ccId.slice(0, 8)}…`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="xl">
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-end gap-2 border-b border-border bg-muted/40 px-4 py-2">
          <Button
            size="sm"
            variant="secondary"
            disabled={!url}
            onClick={() => url && window.open(url, '_blank', 'noopener,noreferrer')}
          >
            <ExternalLink size={14} />
            Abrir en pestaña
          </Button>
        </div>
        <div className="relative flex-1 bg-muted/20">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 animate-spin" size={16} />
              Cargando constancia...
            </div>
          )}
          {errorMessage && !isLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center">
              <AlertCircle className="text-red-400" size={32} />
              <p className="text-sm font-medium text-foreground">No se pudo cargar la constancia</p>
              <p className="text-xs text-muted-foreground">{errorMessage}</p>
            </div>
          )}
          {url && !isLoading && !errorMessage && (
            <iframe
              key={url}
              src={url}
              title={title}
              className="h-full w-full border-0"
            />
          )}
        </div>
      </div>
    </Modal>
  );
}
